import { useEffect, useRef, useState } from "react";

export function useSleepTimer({ onSleepTriggered, content }) {
  const [sleepMode, setSleepMode] = useState("off");
  const [sleepDeadlineAt, setSleepDeadlineAt] = useState(null);
  const [sleepCountdownNow, setSleepCountdownNow] = useState(() => Date.now());

  const sleepModeRef = useRef("off");
  const sleepTimeoutRef = useRef(null);

  sleepModeRef.current = sleepMode;

  useEffect(() => {
    if (!sleepDeadlineAt || !String(sleepMode).startsWith("timer-")) {
      return undefined;
    }

    setSleepCountdownNow(Date.now());
    const intervalId = window.setInterval(() => {
      setSleepCountdownNow(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [sleepDeadlineAt, sleepMode]);

  function clearSleepTimeout() {
    if (sleepTimeoutRef.current) {
      window.clearTimeout(sleepTimeoutRef.current);
      sleepTimeoutRef.current = null;
    }
  }

  function handleSelectSleepTimer(option, setStatus) {
    clearSleepTimeout();

    if (option === "off") {
      setSleepMode("off");
      setSleepDeadlineAt(null);
      return;
    }

    if (option === "chapter") {
      setSleepMode("chapter");
      setSleepDeadlineAt(null);
      setStatus?.(content.statusSleepChapter);
      return;
    }

    const minutes = Number(option);
    const deadlineAt = Date.now() + minutes * 60 * 1000;

    setSleepMode(`timer-${minutes}`);
    setSleepDeadlineAt(deadlineAt);
    setSleepCountdownNow(Date.now());

    sleepTimeoutRef.current = window.setTimeout(() => {
      onSleepTriggered?.();
      setSleepMode("off");
      setSleepDeadlineAt(null);
      setStatus?.(content.statusSleepTriggered);
      sleepTimeoutRef.current = null;
    }, minutes * 60 * 1000);

    setStatus?.(
      (content.statusSleepScheduled || "Sleep in {minutes} min").replace(
        "{minutes}",
        String(minutes)
      )
    );
  }

  function cancelSleepTimer() {
    clearSleepTimeout();
    setSleepMode("off");
    setSleepDeadlineAt(null);
  }

  const sleepCountdownSeconds =
    sleepDeadlineAt && String(sleepMode).startsWith("timer-")
      ? Math.max(0, Math.ceil((sleepDeadlineAt - sleepCountdownNow) / 1000))
      : null;

  return {
    sleepMode,
    sleepModeRef,
    sleepDeadlineAt,
    sleepCountdownNow,
    sleepCountdownSeconds,
    handleSelectSleepTimer,
    cancelSleepTimer,
    clearSleepTimeout
  };
}
